'use client';

import { useState, useEffect } from 'react';
import { BottomSheet } from '@/components/common/BottomSheet';
import { ProfileDetailsForm, ProfileDetailsData } from './ProfileDetailsForm';
import { SportSpecificForm, SportSpecificFormData } from './SportSpecificForms';
import { useAuth } from '@/hooks/useAuth';
import { ChevronLeft } from 'lucide-react';
import { Button } from '@/components/common/Button';

interface EnhancedEnrollmentModalProps {
  isOpen: boolean;
  onClose: () => void;
  sport: string;
  tournamentName: string;
  onSubmit: (data: {
    profileDetails?: ProfileDetailsData;
    sportDetails: SportSpecificFormData;
  }) => void;
  isLoading?: boolean;
}

type EnrollmentStep = 'profile' | 'sport';

export function EnhancedEnrollmentModal({
  isOpen,
  onClose,
  sport,
  tournamentName,
  onSubmit,
  isLoading = false,
}: EnhancedEnrollmentModalProps) {
  const { user } = useAuth();
  const [currentStep, setCurrentStep] = useState<EnrollmentStep>('profile');
  const [profileData, setProfileData] = useState<ProfileDetailsData | null>(
    null
  );
  const [sportData, setSportData] = useState<SportSpecificFormData | null>(
    null
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Check if user already has required profile data
  const hasRequiredProfileData =
    user?.dateOfBirth &&
    user?.gender &&
    user?.countryCode &&
    user?.mobileNumber;

  // Get existing sport details for the current sport
  const existingSportDetails = user?.sportDetails?.[sport.toLowerCase()];

  // Reset step when modal opens, but preserve data during navigation
  useEffect(() => {
    if (isOpen) {
      setCurrentStep(hasRequiredProfileData ? 'sport' : 'profile');
      // Only reset data when modal first opens, not during navigation
      if (!profileData && !sportData) {
        setProfileData(null);
        setSportData(null);
      }
      setIsSubmitting(false);
    } else {
      // Reset all data when modal closes
      setProfileData(null);
      setSportData(null);
    }
  }, [isOpen, hasRequiredProfileData]);

  const handleProfileSubmit = async (data: ProfileDetailsData) => {
    setIsSubmitting(true);

    try {
      setProfileData(data);
      setCurrentStep('sport');
    } catch (error) {
      console.error('Error processing profile data:', error);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleProfileSkip = () => {
    setCurrentStep('sport');
  };

  const handleSportSubmit = async (sportFormData: SportSpecificFormData) => {
    setIsSubmitting(true);

    try {
      setSportData(sportFormData);
      await onSubmit({
        profileDetails: profileData || undefined,
        sportDetails: sportFormData,
      });
    } catch (error) {
      console.error('Error submitting enrollment:', error);
      setIsSubmitting(false);
    }
  };

  const handleBack = () => {
    if (currentStep === 'sport') {
      setCurrentStep('profile');
    }
  };

  const getTitle = () => {
    if (currentStep === 'profile') {
      return hasRequiredProfileData ? 'Verify Profile' : 'Complete Profile';
    }
    return `Enroll in ${tournamentName}`;
  };

  const showBackButton = currentStep === 'sport' && !hasRequiredProfileData;
  const modalIsLoading = isLoading || isSubmitting;

  return (
    <BottomSheet
      isOpen={isOpen}
      onClose={onClose}
      title={getTitle()}
      className="sm:max-w-lg"
      showCloseButton={!modalIsLoading}
    >
      {showBackButton && (
        <div className="mb-4">
          <Button
            variant="light"
            size="sm"
            onClick={handleBack}
            disabled={modalIsLoading}
            leftIcon={<ChevronLeft className="h-4 w-4" />}
          >
            Back
          </Button>
        </div>
      )}

      {currentStep === 'profile' && (
        <ProfileDetailsForm
          onSubmit={handleProfileSubmit}
          onSkip={handleProfileSkip}
          isLoading={modalIsLoading}
          initialData={profileData || undefined}
          existingUserData={{
            name: user?.name || undefined,
            dateOfBirth: user?.dateOfBirth,
            gender: user?.gender,
            phoneNumber:
              user?.countryCode && user?.mobileNumber
                ? `${user.countryCode}${user.mobileNumber}`
                : undefined,
            profilePicture: user?.profilePicture || undefined,
            countryCode: user?.countryCode,
            mobileNumber: user?.mobileNumber,
          }}
        />
      )}

      {currentStep === 'sport' && (
        <SportSpecificForm
          sport={sport}
          onSubmit={handleSportSubmit}
          onCancel={onClose}
          isLoading={modalIsLoading}
          initialData={existingSportDetails || sportData || undefined}
        />
      )}
    </BottomSheet>
  );
}
